import { get, post, put } from './api';

/**
 * The structured configuration stored in EmailAgentConfig.config (JSONB).
 * Fields set via the UI take priority over environment variables at runtime.
 */
export interface AgentConfig {
  ms_user_id?: string;
  sender_email?: string;
  allowed_emails?: string[];
  /** Graph subscription webhook URL; DB value overrides env when set */
  notification_url?: string;
  [key: string]: unknown;
}

export interface AgentSummary {
  agent_type: string;
  is_enabled: boolean;
  status: string;
  last_heartbeat: string | null;
  config: AgentConfig | null;
  is_configured?: boolean;
  missing_fields?: string[];
  processed_today: number;
  success_rate: number;
}

export interface LogEntry {
  id: string;
  agent_type: string;
  event_type: string;
  status: string;
  sender_email: string | null;
  subject: string | null;
  details: Record<string, any> | null;
  error_message: string | null;
  processing_time_ms: number | null;
  created_at: string;
}

export interface LogsResponse {
  items: LogEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface LogsFilter {
  agent_type?: string;
  event_type?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface AgentStats {
  total_today: number;
  success_rate: number;
  avg_processing_time_ms: number | null;
  per_agent: Record<
    string,
    {
      total: number;
      success: number;
      success_rate: number;
      avg_processing_time_ms: number | null;
    }
  >;
  daily_counts: { date: string; count: number }[];
}

export interface AgentDetail {
  agent_type: string;
  is_enabled: boolean;
  status: string;
  last_heartbeat: string | null;
  config: AgentConfig | null;
  is_configured?: boolean;
  missing_fields?: string[];
  runtime: { agent_type: string; running: boolean; has_subscription: boolean };
  recent_logs: {
    id: string;
    event_type: string;
    status: string;
    sender_email: string | null;
    subject: string | null;
    processing_time_ms: number | null;
    created_at: string | null;
  }[];
}

export interface AgentConfigUpdatePayload {
  is_enabled?: boolean;
  ms_user_id?: string;
  sender_email?: string;
  allowed_emails?: string[];
  notification_url?: string;
  config?: Record<string, unknown>;
}

export const emailAgentService = {
  listAgents: () => get<AgentSummary[]>('/api/email-agents'),

  getAgent: (agentType: string) =>
    get<AgentDetail>(`/api/email-agents/${agentType}`),

  updateConfig: (agentType: string, data: AgentConfigUpdatePayload) =>
    put(`/api/email-agents/${agentType}`, data),

  startAgent: (agentType: string) =>
    post<{ status: string; agent_type: string }>(`/api/email-agents/${agentType}/start`),

  stopAgent: (agentType: string) =>
    post<{ status: string; agent_type: string }>(`/api/email-agents/${agentType}/stop`),

  getLogs: (filters: LogsFilter = {}) =>
    get<LogsResponse>('/api/email-agents/logs', filters),

  getLogDetail: (logId: string) =>
    get<LogEntry>(`/api/email-agents/logs/${logId}`),

  getStats: () => get<AgentStats>('/api/email-agents/logs/stats'),
};
